import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import {
    HeadingText50,
    ComponentTextMedium,
    SkeletonPlaceholder,
    BodyTextMedium as BodyTextMediumBase,
    Button as ButtonBase,
    ButtonGroup,
    pxToRem,
} from '@modernhealth/vital-components';

import googleIcon from '../assets/google-icon.svg';
import calendarIcon from '../assets/calendar-icon.svg';
import calendarImage from 'src/public/ds/illustrations/calendar.svg';
import { DailyPauseCalendarUrls } from 'src/modules/scheduledContent/hooks/useDailyPauseReminderCalendarUrls';

type EventCalendarModalContentProps = {
    handleCalendarButtonClick: (param: DailyPauseCalendarUrls) => void;
    loading: boolean;
};

export const EventCalendarModalContent: React.FC<EventCalendarModalContentProps> = ({
    handleCalendarButtonClick,
    loading,
}) => {
    const { t } = useTranslation();

    return (
        <>
            <CalendarImageContainer>
                <CalendarImage src={calendarImage} />
            </CalendarImageContainer>
            <HeadingText50 marginBottom={12} marginTop={30} centered>
                {t('dailyPause:additionalOptions.select_calendar.title')}
            </HeadingText50>
            <BodyTextMedium marginBottom={40} centered maxWidth="420px">
                {t('dailyPause:additionalOptions.select_calendar.description')}
            </BodyTextMedium>
            {loading ? (
                <>
                    <SkeletonPlaceholder width="100%" height={48} marginBottom={14} />
                    <SkeletonPlaceholder width="100%" height={48} />
                </>
            ) : (
                <ButtonGroup>
                    <Button
                        variant="secondary"
                        size="large"
                        onClick={() => handleCalendarButtonClick('googleCalendarUrl')}
                    >
                        <ButtonIcon src={googleIcon} alt="google icon" />
                        <ComponentTextMedium fontColor="gray65">
                            {t('dailyPause:additionalOptions.add_to_google_calendar')}
                        </ComponentTextMedium>
                    </Button>
                    <Button
                        variant="secondary"
                        size="large"
                        onClick={() => handleCalendarButtonClick('icsUrl')}
                    >
                        <ButtonIcon src={calendarIcon} alt="calendar icon" />
                        <ComponentTextMedium fontColor="gray65">
                            {t('dailyPause:additionalOptions.add_to_calendar_ics')}
                        </ComponentTextMedium>
                    </Button>
                </ButtonGroup>
            )}
        </>
    );
};

const Button = styled(ButtonBase)({
    paddingLeft: 0,
    paddingRight: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    maxHeight: pxToRem(48),
});

const ButtonIcon = styled.img();

const CalendarImageContainer = styled.div({
    display: 'flex',
    justifyContent: 'center',
});

const CalendarImage = styled.img({
    height: pxToRem(95),
});

const BodyTextMedium = styled(BodyTextMediumBase)({
    marginLeft: 'auto',
    marginRight: 'auto',
});
